// ============================================================================
// GAME ITEMS MENU COMPONENT
// ============================================================================
// Side panel for purchasing and using in-game items
//  UPDATED: Supports restricted state pricing

//  ENTERPRISE: iOS WebView compatibility with timeout handling

// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Gift, X, Megaphone, Target, Sparkles, Wallet } from 'lucide-react';
import { GameItem, THROWABLE_ITEMS, SELF_ITEMS, getThrowableItemsWithPricing, getSelfItemsWithPricing, getTauntItemsWithPricing } from '../types/gameItemsTypes';
import gameItemsService from '../services/gameItemsService';
import geoService from '../services/geoService';
import './GameItemsMenu.css';

interface TargetPlayer {
  id: string;
  name: string;
}

interface GameItemsMenuProps {
  tableId: string;
  currentUserId: string;
  currentUserName: string;
  players: TargetPlayer[];  // Other players (targets for throwables)
  pkCashBalance: number;
  onClose: () => void;
  onBalanceChange?: (newBalance: number) => void;
}

type TabType = 'throw' | 'self' | 'taunt';

//  NEW: Extended item type with effective pricing

interface PricedGameItem extends GameItem {
  effectivePrice: number;
  effectiveCooldown: number;
}

const GameItemsMenu: React.FC<GameItemsMenuProps> = ({
  tableId,
  currentUserId,
  currentUserName,
  players,
  pkCashBalance,
  onClose,
  onBalanceChange,
}) => {
  // Default to Taunts so players see the free options first - paid items
  // shouldn't be the landing tab.
  const [activeTab, setActiveTab] = useState<TabType>('taunt');
  const [selectedItem, setSelectedItem] = useState<PricedGameItem | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<TargetPlayer | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cooldowns, setCooldowns] = useState<Map<string, number>>(new Map());
  
  //  NEW: Geo state for pricing

  const [isRestrictedState, setIsRestrictedState] = useState(false);

  // Safe balance value - default to 0 if undefined/null
  const safeBalance = pkCashBalance ?? 0;

  // Get other players (not current user)
  const otherPlayers = useMemo(() => {
    return (players || []).filter(p => p && p.id !== currentUserId);
  }, [players, currentUserId]);

  //  NEW: Get items with state-adjusted pricing

  const throwableItems = useMemo(() => getThrowableItemsWithPricing(isRestrictedState), [isRestrictedState]);
  const selfItems = useMemo(() => getSelfItemsWithPricing(isRestrictedState), [isRestrictedState]);
  const tauntItems = useMemo(() => getTauntItemsWithPricing(isRestrictedState), [isRestrictedState]);

  //  NEW: Check geo on mount

  useEffect(() => {
    const checkGeo = async () => {
      try {
        const result = await geoService.checkLocation();
        setIsRestrictedState(result.isRestrictedState === true);
      } catch (error) {
        console.error('Error checking geo:', error);
      }
    };
    checkGeo();
  }, []);

  // Update cooldowns every second
  useEffect(() => {
    const updateCooldowns = () => {
      const stored = localStorage.getItem(`itemCooldowns_${currentUserId}`);
      if (!stored) return;

      try {
        const cooldownData = JSON.parse(stored);
        const now = Date.now();
        const newCooldowns = new Map<string, number>();

        Object.entries(cooldownData).forEach(([itemId, endTime]) => {
          const remaining = Math.ceil(((endTime as number) - now) / 1000);
          if (remaining > 0) {
            newCooldowns.set(itemId, remaining);
          }
        });
        setCooldowns(newCooldowns);
      } catch (e) {
        console.error('Error parsing cooldowns:', e);
      }
    };

    updateCooldowns();
    const interval = setInterval(updateCooldowns, 1000);
    return () => clearInterval(interval);
  }, [currentUserId]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const handleItemSelect = useCallback((item: PricedGameItem) => {
    // Check cooldown
    if (cooldowns.has(item.id)) {
      setError(`Wait ${cooldowns.get(item.id)}s before using again`);
      return;
    }

    // Check balance using effective price
    if (safeBalance < item.effectivePrice) {
      setError(`Need PSS Coins`);
      return;
    }
    setSelectedItem(item);
    setError(null);

    // Auto-select target where applicable:
    //  - 'self' items target the caller (emote on own avatar).
    //  - 'taunt' items broadcast to the whole table; target is the caller
    //    so server logic that requires a target field still validates,
    //    but the UI renders the animation as a center-stage shout.
    //  - 'throwable' items need the user to pick a target.
    if (item.category === 'self' || item.category === 'taunt') {
      setSelectedTarget({ id: currentUserId, name: currentUserName });
    } else {
      setSelectedTarget(null);
    }
  }, [cooldowns, safeBalance, currentUserId, currentUserName]);

  const handleTargetSelect = useCallback((player: TargetPlayer) => {
    setSelectedTarget(player);
  }, []);

  //  ENTERPRISE: Updated with timeout handling for iOS WebView

  const handleUseItem = useCallback(async () => {
    if (!selectedItem || !selectedTarget) return;

    setIsProcessing(true);
    setError(null);

    try {
      //  ENTERPRISE: Add timeout for iOS WebView (15 seconds)

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout - please try again')), 15000);
      });

      const purchasePromise = gameItemsService.useItem(
        tableId,
        selectedItem.id,
        currentUserId,
        currentUserName,
        selectedTarget.id,
        selectedTarget.name,
        isRestrictedState
      );

      // Race between purchase and timeout
      const result = await Promise.race([purchasePromise, timeoutPromise]);

      if (result.success) {
        setSuccess(`${selectedItem.emoji} sent to ${selectedTarget.name}!`);
        setSelectedItem(null);
        setSelectedTarget(null);
        if (onBalanceChange && result.newBalance !== undefined) {
          onBalanceChange(result.newBalance);
        }
        
        // Set cooldown in localStorage using effective cooldown
        const stored = localStorage.getItem(`itemCooldowns_${currentUserId}`) || '{}';
        try {
          const cooldownData = JSON.parse(stored);
          cooldownData[selectedItem.id] = Date.now() + selectedItem.effectiveCooldown;
          localStorage.setItem(`itemCooldowns_${currentUserId}`, JSON.stringify(cooldownData));
        } catch (e) {
          console.error('Error saving cooldown:', e);
        }
        
        //  AUTO-CLOSE: Close modal after 500ms so user can see the animation

        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setError(result.error || 'Failed to use item');
      }
    } catch (err: any) {
      console.error('❌ [GameItemsMenu] Error:', err);
      setError(err.message || 'Failed to use item - please try again');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedItem, selectedTarget, tableId, currentUserId, currentUserName, onBalanceChange, isRestrictedState, onClose]);

  const renderItemButton = useCallback((item: PricedGameItem) => {
    const cooldown = cooldowns.get(item.id);
    const canAfford = safeBalance >= item.effectivePrice; //  Use effective price

    const isSelected = selectedItem?.id === item.id;
    const isDisabled = !!cooldown || !canAfford;

    return (
      <button
        key={item.id}
        className={`item-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={() => handleItemSelect(item)}
        disabled={isDisabled}
        title={item.description}
      >
        <span className="item-emoji">{item.emoji}</span>
        <span className="item-name">{item.name}</span>
        <span className={`item-price ${!canAfford ? 'cant-afford' : ''}`}>
          {cooldown ? `${cooldown}s` : `$${item.effectivePrice.toFixed(2)}`}
        </span>
      </button>
    );
  }, [cooldowns, safeBalance, selectedItem, handleItemSelect]);

  return (
    <div className="game-items-overlay">
      <div className="game-items-menu">
        {/* Header */}
        <div className="menu-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Gift size={16} /> Items & Actions
          </h3>
          <div className="balance-display" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Wallet size={14} /> ${safeBalance.toFixed(2)}
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="menu-tabs">
          <button
            className={`tab-btn ${activeTab === 'taunt' ? 'active' : ''}`}
            onClick={() => setActiveTab('taunt')}
          >
            <Megaphone size={13} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
            Taunt (Free)
          </button>
          <button
            className={`tab-btn ${activeTab === 'throw' ? 'active' : ''}`}
            onClick={() => setActiveTab('throw')}
          >
            <Target size={13} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
            Throw
          </button>
          <button
            className={`tab-btn ${activeTab === 'self' ? 'active' : ''}`}
            onClick={() => setActiveTab('self')}
          >
            <Sparkles size={13} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
            Actions
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && <div className="menu-error">{error}</div>}
        {success && <div className="menu-success">{success}</div>}
        
        {/*  NEW: Restricted state notice */}

        {isRestrictedState && (
          <div className="menu-notice" style={{ background: 'rgba(159, 122, 234, 0.1)', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '12px', color: '#9F7AEA' }}>
             Premium pricing active in your state

          </div>
        )}

        {/* Items Grid -  UPDATED: Use state-aware item lists */}

        <div className="items-grid">
          {activeTab === 'throw' && throwableItems.map(renderItemButton)}
          {activeTab === 'self' && selfItems.map(renderItemButton)}
          {activeTab === 'taunt' && tauntItems.map(renderItemButton)}
        </div>

        {/* Target Selection (for throwables) */}
        {selectedItem && selectedItem.category === 'throwable' && (
          <div className="target-selection">
            <h4>Select Target:</h4>
            <div className="target-list">
              {otherPlayers.length === 0 ? (
                <div className="no-targets">No other players at table</div>
              ) : (
                otherPlayers.map(player => (
                  <button
                    key={player.id}
                    className={`target-btn ${selectedTarget?.id === player.id ? 'selected' : ''}`}
                    onClick={() => handleTargetSelect(player)}
                  >
                    <span className="target-name">{player.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        {selectedItem && (selectedItem.category === 'self' || selectedTarget) && (
          <div className="action-section">
            <div className="action-preview">
              <span className="preview-item">{selectedItem.emoji} {selectedItem.name}</span>
              {selectedTarget && selectedItem.category === 'throwable' && (
                <span className="preview-target">→ {selectedTarget.name}</span>
              )}
              <span className="preview-price">-${selectedItem.effectivePrice.toFixed(2)}</span>
            </div>
            <button
              className="use-item-btn"
              onClick={handleUseItem}
              disabled={isProcessing}
            >
              {isProcessing ? 'Sending...' : `Send ${selectedItem.emoji}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameItemsMenu;